import { SITE_TIME_ZONE } from "@/lib/site-time";
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AdminNav } from "@/components/admin/AdminNav";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { adminListQuotes, adminUpdateQuote, QUOTE_STATUSES, type QuoteStatus } from "@/lib/quote-admin.functions";

const quotesQuery = queryOptions({ queryKey: ["admin-quotes"], queryFn: () => adminListQuotes() });

const STATUS_LABEL: Record<QuoteStatus, string> = { new: "Nieuw", in_behandeling: "In behandeling", afgerond: "Afgerond" };
const TYPE_LABEL: Record<string, string> = {
  nieuwbouw: "Nieuwbouw", renovatie: "Renovatie", techniek: "Zwembadtechniek", warmtepomp: "Warmtepomp", overige: "Overige",
};
const CONTACT_LABEL: Record<string, string> = { email: "E-mail", phone: "Telefoon", whatsapp: "WhatsApp" };

export const Route = createFileRoute("/_authenticated/admin/aanvragen")({
  staticData: { sitemap: false },
  head: () => ({ meta: [
    { title: "Offerte-aanvragen — Gopro Projects" },
    { name: "description", content: "Binnengekomen offerte-aanvragen bekijken en opvolgen." },
    { name: "robots", content: "noindex, nofollow" },
    { property: "og:title", content: "Offerte-aanvragen — Gopro Projects" },
    { property: "og:description", content: "Binnengekomen offerte-aanvragen bekijken en opvolgen." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: AdminQuotesPage,
});

type Quote = Awaited<ReturnType<typeof adminListQuotes>>[number];

function AdminQuotesPage() {
  const [filter, setFilter] = useState<"alle" | QuoteStatus>("alle");
  const { data, isLoading, isError } = useQuery(quotesQuery);
  const list = data?.filter((q) => filter === "alle" || q.status === filter) ?? [];
  const nieuw = data?.filter((q) => q.status === "new").length ?? 0;

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6">
      <AdminNav />
      <h1 className="text-3xl font-extrabold">Offerte-aanvragen</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {nieuw === 1 ? "1 nieuwe aanvraag." : `${nieuw} nieuwe aanvragen.`}
      </p>
      <div className="mt-8 flex flex-wrap gap-2">
        {(["alle", ...QUOTE_STATUSES] as const).map((value) => (
          <Button key={value} size="sm" variant={filter === value ? "default" : "outline"} onClick={() => setFilter(value)}>
            {value === "alle" ? "Alle" : STATUS_LABEL[value]}
          </Button>
        ))}
      </div>
      {isLoading && <p className="mt-5 text-sm text-muted-foreground">Aanvragen laden…</p>}
      {isError && <p className="mt-5 text-sm text-destructive">Aanvragen konden niet geladen worden.</p>}
      {!isLoading && !isError && list.length === 0 && (
        <p className="mt-5 text-sm text-muted-foreground">Geen aanvragen in deze selectie.</p>
      )}
      <div className="mt-5 space-y-3">
        {list.map((q) => <QuoteCard key={q.id} quote={q} />)}
      </div>
    </main>
  );
}

function QuoteCard({ quote }: { quote: Quote }) {
  const queryClient = useQueryClient();
  const status = (QUOTE_STATUSES as readonly string[]).includes(quote.status) ? (quote.status as QuoteStatus) : "new";
  const [notes, setNotes] = useState(quote.notes ?? "");
  const save = useMutation({
    mutationFn: (input: { status: QuoteStatus; notes: string }) => adminUpdateQuote({ data: { id: quote.id, ...input } }),
    onSuccess: () => {
      toast.success("Aanvraag bijgewerkt.");
      void queryClient.invalidateQueries({ queryKey: ["admin-quotes"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Opslaan is niet gelukt."),
  });
  const rows: [string, string | null][] = [
    ["Type zwembad", quote.pool_type], ["Afmetingen", quote.dimensions], ["Leeftijd", quote.age],
    ["Locatie", quote.location], ["Adres", quote.address],
    ["Voorkeur contact", CONTACT_LABEL[quote.preferred_contact] ?? quote.preferred_contact],
  ];

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap gap-2">
              <Badge variant={status === "new" ? "default" : status === "afgerond" ? "outline" : "secondary"}>{STATUS_LABEL[status]}</Badge>
              <Badge variant="secondary">{TYPE_LABEL[quote.type] ?? quote.type}</Badge>
            </div>
            <h2 className="mt-3 font-semibold">{quote.first_name} {quote.last_name}</h2>
            <p className="mt-1 text-sm">
              <a className="text-primary hover:underline" href={`mailto:${quote.email}`}>{quote.email}</a>
              {quote.phone && <> · <a className="text-primary hover:underline" href={`tel:${quote.phone}`}>{quote.phone}</a></>}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {new Intl.DateTimeFormat("nl-BE", { dateStyle: "medium", timeStyle: "short", timeZone: SITE_TIME_ZONE }).format(new Date(quote.created_at))}
            </p>
          </div>
          <label className="text-sm">
            <span className="mr-2">Status</span>
            <select
              className="rounded border border-input bg-background px-2 py-1"
              value={status}
              disabled={save.isPending}
              onChange={(e) => save.mutate({ status: e.target.value as QuoteStatus, notes })}
            >
              {QUOTE_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
          </label>
        </div>
        <dl className="mt-4 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          {rows.filter(([, v]) => v).map(([k, v]) => (
            <div key={k} className="flex gap-2"><dt className="text-muted-foreground">{k}:</dt><dd>{v}</dd></div>
          ))}
        </dl>
        {quote.description && <p className="mt-4 whitespace-pre-wrap text-sm">{quote.description}</p>}
        {quote.photoUrls.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {quote.photoUrls.map((url, i) => (
              <a key={url} href={url} target="_blank" rel="noreferrer">
                <img src={url} alt={`Foto ${i + 1} bij aanvraag van ${quote.first_name}`} className="h-24 w-32 rounded-lg object-cover" loading="lazy" />
              </a>
            ))}
          </div>
        )}
        <label className="mt-4 block text-sm">
          <span className="text-muted-foreground">Notities</span>
          <Textarea className="mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        </label>
        <Button className="mt-2" size="sm" disabled={save.isPending} onClick={() => save.mutate({ status, notes })}>
          {save.isPending ? "Bezig met opslaan…" : "Bewaar notities"}
        </Button>
      </CardContent>
    </Card>
  );
}
